'use client'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useActiveLeague, useLeagueMonthlyLeaderboard } from '../hooks/useFootball'
import { airdropApi, horseApi, userApi } from '../api/endpoints'
import LuckyIcon from '../components/lucky/LuckyIcon'
import { TrophyGlyph } from '../components/lucky/ProfileGlyphs'
import { formatNumber } from '../utils/helpers'
import { useLocale, useT } from '../contexts/LocaleContext'
import { LOCALE_META } from '../i18n'

type TopTab = 'football' | 'horse' | 'airdrop' | 'invite'

function TopUserRow({
  rank,
  photoUrl,
  firstName,
  lastName,
  score,
  scoreLabel,
}: {
  rank: number
  photoUrl?: string
  firstName?: string
  lastName?: string
  score: number
  scoreLabel: string
}) {
  const t = useT()
  const { locale } = useLocale()
  const numberLocale = LOCALE_META[locale].numberLocale
  const displayName = [firstName, lastName].filter(Boolean).join(' ').trim() || t('common.user')

  return (
    <div className="lucky-profile__row">
      <span className="w-9 flex justify-center">
        {rank <= 3 ? (
          <TrophyGlyph size={28} />
        ) : (
          <span className="text-sm font-bold text-[#8fa8d4]">#{rank}</span>
        )}
      </span>
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={displayName}
          className="w-9 h-9 rounded-full object-cover border border-[rgba(246,200,76,0.35)]"
        />
      ) : (
        <LuckyIcon name="profile" size={36} className="rounded-full" />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm truncate">{displayName}</p>
      </div>
      <div className="text-left">
        <p className="font-extrabold text-sm text-[#f6c84c]">{formatNumber(score, numberLocale)}</p>
        <p className="text-[10px] text-[#8fa8d4]">{scoreLabel}</p>
      </div>
    </div>
  )
}

export default function TopUsersPage() {
  const navigate = useNavigate()
  const t = useT()
  const { dir } = useLocale()
  const [topTab, setTopTab] = useState<TopTab>('football')

  const TOP_TABS: { id: TopTab; label: string }[] = [
    { id: 'football', label: t('nav.football') },
    { id: 'horse', label: t('nav.horse') },
    { id: 'airdrop', label: t('nav.airdrop') },
    { id: 'invite', label: t('profile.invites') },
  ]

  const { data: activeLeagueData } = useActiveLeague('league')
  const footballLeagueId = activeLeagueData?.league?.id || 0
  const { data: footballBoardData, isLoading: footballLoading } = useLeagueMonthlyLeaderboard(footballLeagueId, 10)
  const footballLeaders = footballBoardData?.leaderboard || []

  const { data: horseBoardData, isLoading: horseLoading } = useQuery({
    queryKey: ['horse', 'league', 'leaderboard', 10],
    queryFn: () => horseApi.leagueLeaderboard(undefined, 10),
    enabled: topTab === 'horse',
  })
  const horseLeaders = horseBoardData?.leaderboard || []

  const { data: airdropBoardData, isLoading: airdropLoading } = useQuery({
    queryKey: ['airdrop', 'monthly-leaderboard', 10],
    queryFn: () => airdropApi.getMonthlyLeaderboard(undefined, 10),
    enabled: topTab === 'airdrop',
  })
  const airdropLeaders = (airdropBoardData?.winners || []).slice(0, 10)

  const { data: inviteBoardData, isLoading: inviteLoading } = useQuery({
    queryKey: ['users', 'top-referrers', 10],
    queryFn: () => userApi.getTopReferrers(10),
    enabled: topTab === 'invite',
  })
  const inviteLeaders = inviteBoardData?.users || []

  const topLoading =
    (topTab === 'football' && footballLoading) ||
    (topTab === 'horse' && horseLoading) ||
    (topTab === 'airdrop' && airdropLoading) ||
    (topTab === 'invite' && inviteLoading)

  const topEmpty =
    (topTab === 'football' && footballLeaders.length === 0) ||
    (topTab === 'horse' && horseLeaders.length === 0) ||
    (topTab === 'airdrop' && airdropLeaders.length === 0) ||
    (topTab === 'invite' && inviteLeaders.length === 0)

  const emptyMessage =
    topTab === 'football'
      ? t('topUsers.emptyFootball')
      : topTab === 'horse'
        ? t('topUsers.emptyHorse')
        : topTab === 'airdrop'
          ? t('topUsers.emptyAirdrop')
          : t('topUsers.emptyInvite')

  return (
    <div className="lucky-profile mini-app-container pb-20">
      <div className="p-4 pt-14">
        <header className="lucky-profile__page-header">
          <button
            type="button"
            className="lucky-profile__back"
            onClick={() => navigate('/profile')}
            aria-label={t('common.back')}
          >
            {dir === 'rtl' ? '→' : '←'}
          </button>
          <div className="lucky-profile__page-header-icon">
            <TrophyGlyph size={28} />
          </div>
          <div>
            <h1>{t('topUsers.title')}</h1>
            <p>{t('topUsers.subtitle')}</p>
          </div>
        </header>

        <section className="lucky-profile__card">
          <div className="lucky-profile__tabs">
            {TOP_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`lucky-profile__tab ${topTab === tab.id ? 'is-on' : ''}`}
                onClick={() => setTopTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {topLoading ? (
            <p className="lucky-profile__empty">{t('common.loading')}</p>
          ) : topEmpty ? (
            <p className="lucky-profile__empty">{emptyMessage}</p>
          ) : (
            <div className="lucky-profile__list">
              {topTab === 'football' &&
                footballLeaders.map((entry, index) => (
                  <TopUserRow
                    key={entry.id || entry.user_id}
                    rank={index + 1}
                    photoUrl={entry.user?.photo_url}
                    firstName={entry.user?.first_name}
                    lastName={entry.user?.last_name}
                    score={entry.monthly_score}
                    scoreLabel={t('topUsers.scoreLabel')}
                  />
                ))}
              {topTab === 'horse' &&
                horseLeaders.map((entry, index) => (
                  <TopUserRow
                    key={entry.user_id}
                    rank={index + 1}
                    photoUrl={entry.user?.photo_url}
                    firstName={entry.user?.first_name}
                    lastName={entry.user?.last_name}
                    score={entry.total_score}
                    scoreLabel={t('topUsers.scoreLabel')}
                  />
                ))}
              {topTab === 'airdrop' &&
                airdropLeaders.map((entry, index) => (
                  <TopUserRow
                    key={entry.user_id}
                    rank={entry.rank || index + 1}
                    photoUrl={entry.photo_url}
                    firstName={entry.first_name}
                    score={entry.score}
                    scoreLabel={t('topUsers.scoreLabel')}
                  />
                ))}
              {topTab === 'invite' &&
                inviteLeaders.map((leader, index) => (
                  <TopUserRow
                    key={leader.id}
                    rank={index + 1}
                    photoUrl={leader.photo_url}
                    firstName={leader.first_name}
                    lastName={leader.last_name}
                    score={leader.referral_count}
                    scoreLabel={t('topUsers.inviteLabel')}
                  />
                ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
